import { getAllDrafts, getSubmissions } from '../db'
import { isAuthExpiredError } from '../authErrors'
import { dispatchUploadProgressEvent } from '../uploadEvents'
import type { DraftRecording, Submission } from '@/types'
import { uploadChunkedDraft } from './draftSync'
import { runImmediateSyncPreflight, runSyncAllPreflight } from './preflight'
import { syncSubmission } from './submissionSync'

export interface SyncResult {
  success: boolean
  synced: number
  failed: number
  errors: string[]
}

async function syncDraft(draft: DraftRecording): Promise<boolean> {
  return uploadChunkedDraft(draft)
}

/**
 * Sync all unsynced submissions and drafts to the backend
 */
export async function syncAll(): Promise<SyncResult> {
  const result: SyncResult = {
    success: true,
    synced: 0,
    failed: 0,
    errors: [],
  }

  const preflight = await runSyncAllPreflight()
  if (!preflight.ok) {
    result.success = false
    result.errors.push(preflight.error)
    dispatchUploadProgressEvent({ type: 'error', message: preflight.eventMessage })
    return result
  }

  try {
    // Get all unsynced submissions. Skip ones the server terminally rejected
    // (uploadStatus 'failed', e.g. asset window closed) so they aren't retried
    // on every sync.
    const submissions = await getSubmissions()
    const unsyncedSubmissions = submissions.filter(
      (s) => (!s.syncedAt || s.syncedAt === null) && s.uploadStatus !== 'failed',
    )
    const drafts = await getAllDrafts()
    const syncedSubmissionIds = new Set(
      submissions.filter((submission) => Boolean(submission.syncedAt)).map((submission) => submission.id),
    )
    const draftsToSync = drafts.filter((draft) => !syncedSubmissionIds.has(draft.id))
    const totalToSync =
      unsyncedSubmissions.length +
      draftsToSync.reduce((sum, draft) => sum + (draft.chunkIds?.length || 1), 0)
    if (totalToSync > 0) {
      dispatchUploadProgressEvent({ type: 'start', total: totalToSync })
    } else {
      dispatchUploadProgressEvent({ type: 'complete', total: 0, failed: 0 })
      return result
    }

    // Sync submissions (note: audio submissions need blob retrieval)
    let processed = 0
    for (const submission of unsyncedSubmissions) {
      let audioBlob: Blob | undefined

      if (submission.type === 'audio') {
        // Use localBlob if available (preferred - already in memory)
        if (submission.localBlob) {
          audioBlob = submission.localBlob
        } else if (submission.audioBlobKey) {
          // Retrieve blob from IndexedDB if needed
          // Note: This requires additional implementation to retrieve blobs
          // For now, we'll skip audio submissions that don't have the blob available
          console.warn(`Skipping audio submission ${submission.id} - blob retrieval not implemented`)
          continue
        } else {
          console.warn(`Skipping audio submission ${submission.id} - no blob available`)
          continue
        }
      }

      processed += 1
      dispatchUploadProgressEvent({
        type: 'progress',
        id: submission.id,
        pct: Math.round((processed / totalToSync) * 100),
      })

      let success = false
      try {
        success = await syncSubmission(submission, audioBlob)
      } catch (error) {
        if (isAuthExpiredError(error)) {
          result.success = false
          result.failed += 1
          result.errors.push(error.message)
          dispatchUploadProgressEvent({ type: 'error', message: 'auth required' })
          return result
        }
        throw error
      }
      if (success) {
        result.synced++
      } else {
        result.failed++
        result.errors.push(`Failed to sync submission ${submission.id}`)
      }
      dispatchUploadProgressEvent({ type: 'refresh' })
    }

    for (const draft of draftsToSync) {
      let success = false
      try {
        processed += 1
        dispatchUploadProgressEvent({
          type: 'progress',
          id: draft.id,
          pct: Math.round((processed / totalToSync) * 100),
        })
        success = await syncDraft(draft)
      } catch (error) {
        if (isAuthExpiredError(error)) {
          result.success = false
          result.failed += 1
          result.errors.push(error.message)
          dispatchUploadProgressEvent({ type: 'error', message: 'auth required' })
          return result
        }
        throw error
      }
      if (success) {
        result.synced++
      } else {
        result.failed++
        result.errors.push(`Failed to sync draft ${draft.id}`)
      }
      dispatchUploadProgressEvent({ type: 'refresh' })
    }

    result.success = result.failed === 0
    if (result.success) {
      dispatchUploadProgressEvent({
        type: 'complete',
        total: totalToSync,
        failed: result.failed,
      })
    } else {
      dispatchUploadProgressEvent({
        type: 'error',
        message: `Failed to sync ${result.failed} of ${totalToSync} items`,
      })
    }
  } catch (error) {
    result.success = false
    result.errors.push(`Sync error: ${error instanceof Error ? error.message : String(error)}`)
    dispatchUploadProgressEvent({ type: 'error', message: 'sync failure' })
  }

  return result
}

/**
 * Sync a single submission immediately (called after saving)
 */
export async function syncSubmissionImmediately(
  submission: Submission,
  audioBlob?: Blob,
): Promise<boolean> {
  const preflightOk = await runImmediateSyncPreflight()
  if (!preflightOk) {
    return false
  }

  try {
    return await syncSubmission(submission, audioBlob)
  } catch (error) {
    if (isAuthExpiredError(error)) {
      return false
    }
    throw error
  }
}
